import React, { useState, useEffect } from 'react';
// Header is provided by AuthenticatedRoute wrapper
import { supabase } from '../supabase/client';

const CampaignPerformance: React.FC = () => {
  const [dashboardUrl, setDashboardUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardUrl = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('campaign_dashboard_url')
          .eq('id', session.user.id)
          .single();

        if (userError) {
          setError('Failed to fetch dashboard URL');
        } else {
          setDashboardUrl(userData?.campaign_dashboard_url || null);
        }
      } catch (error) {
        console.error('Error fetching dashboard URL:', error);
        setError('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardUrl();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
          <div className="flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading campaign dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Campaign Performance Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor your campaign metrics and performance</p>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Dashboard</h3>
            <p className="text-red-700">{error}</p>
          </div>
        ) : dashboardUrl ? (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src={dashboardUrl}
                className="w-full h-screen min-h-[800px]"
                frameBorder="0"
                title="Campaign Performance Dashboard"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <div className="text-yellow-500 text-6xl mb-4">📊</div>
            <h3 className="text-xl font-medium text-yellow-900 mb-2">Dashboard Not Set Up</h3>
            <p className="text-yellow-800 mb-4">
              Your campaign performance dashboard hasn't been configured yet.
            </p>
            <p className="text-yellow-700 text-sm">
              Please contact support to set up your personalized campaign dashboard.
            </p>
            <div className="mt-6">
              <a
                href="/profile"
                className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                <span className="mr-2">👤</span>
                Go to Profile
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignPerformance;